import {
	type HTMLAttributes,
	type Ref,
	forwardRef,
	useLayoutEffect,
	useRef,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../cn.js";

const GAP = 6;
const MARGIN = 8;

/** `showModal()` puts the dialog in the top layer; body portaled menus sit underneath it. */
function portalTargetFor(anchor: HTMLElement | null): HTMLElement {
	const dialog = anchor?.closest("dialog");
	if (dialog instanceof HTMLDialogElement && dialog.open) return dialog;
	return document.body;
}

export interface AnchoredPanelProps extends HTMLAttributes<HTMLDivElement> {
	open: boolean;
	anchorRef: { readonly current: HTMLElement | null };
	align?: "start" | "end";
	/** Match the anchor width. Selects use this so the list lines up with the field. */
	matchWidth?: boolean;
	/** Outside press. Escape is left to the panel so it can return focus itself. */
	onDismiss?: () => void;
}

/**
 * Dropdown surface rendered on `document.body`.
 *
 * Page shells use `overflow: hidden`, which clips an absolutely positioned
 * menu. Measuring against the viewport and leaving a gap keeps the panel off
 * the control that opened it and inside the screen.
 */
export const AnchoredPanel = forwardRef<HTMLDivElement, AnchoredPanelProps>(function AnchoredPanel(
	{ open, anchorRef, align = "start", matchWidth = false, onDismiss, className, style, children, ...props },
	forwardedRef,
) {
	const panelRef = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		if (!open) return;
		const panel = panelRef.current;
		const anchor = anchorRef.current;
		if (!panel || !anchor) return;

		const place = () => {
			const box = anchor.getBoundingClientRect();
			const naturalWidth = matchWidth ? box.width : Math.max(panel.scrollWidth, box.width);
			const width = Math.min(naturalWidth, window.innerWidth - MARGIN * 2);
			const spaceBelow = window.innerHeight - box.bottom - GAP - MARGIN;
			const spaceAbove = box.top - GAP - MARGIN;
			const contentHeight = panel.scrollHeight;
			const openBelow = spaceBelow >= Math.min(contentHeight, 160) || spaceBelow >= spaceAbove;
			const maxHeight = Math.max(96, Math.floor(openBelow ? spaceBelow : spaceAbove));
			const height = Math.min(contentHeight, maxHeight);
			const top = openBelow ? box.bottom + GAP : Math.max(MARGIN, box.top - GAP - height);
			let left = align === "end" ? box.right - width : box.left;
			left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, window.innerWidth - width - MARGIN));

			const appliedWidth = matchWidth || naturalWidth > window.innerWidth - MARGIN * 2 ? `${width}px` : "";
			panel.style.position = "fixed";
			panel.style.top = `${top}px`;
			panel.style.left = `${left}px`;
			panel.style.width = appliedWidth;
			panel.style.maxHeight = `${maxHeight}px`;
			panel.style.zIndex = "70";
			panel.style.visibility = "visible";
		};

		place();
		const observer = new ResizeObserver(place);
		observer.observe(panel);
		window.addEventListener("resize", place);
		window.addEventListener("scroll", place, true);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", place);
			window.removeEventListener("scroll", place, true);
		};
	}, [open, align, matchWidth, anchorRef]);

	useLayoutEffect(() => {
		if (!open) return;

		const onPointerDown = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
			// Nested menus (e.g. Add to contacts inside message details) portaled separately.
			if (target instanceof Element && target.closest("[data-anchored-panel]")) return;
			onDismiss?.();
		};

		document.addEventListener("pointerdown", onPointerDown);
		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [open, anchorRef, onDismiss]);

	if (!open || typeof document === "undefined") return null;

	const portalTarget = portalTargetFor(anchorRef.current);

	return createPortal(
		<div
			{...props}
			data-anchored-panel=""
			ref={(node) => {
				panelRef.current = node;
				assignRef(forwardedRef, node);
			}}
			style={{ position: "fixed", visibility: "hidden", ...style }}
			className={cn(
				"overflow-y-auto overscroll-contain rounded-(--radius-lg) border border-(--border-subtle)",
				"bg-(--surface-raised) p-1 shadow-(--shadow-overlay)",
				className,
			)}
		>
			{children}
		</div>,
		portalTarget,
	);
});

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
